import type { EdgeId, NodeId } from '@ipfabric/utils';
import { callMethod } from '@ipfabric/utils/fp/function';
import { toggleHideEdge } from 'components/Graph/GraphApp/GraphEdge';
import { hideNode, unhideNode } from 'components/Graph/GraphApp/GraphNode';
import type { GraphRendererPlugin } from 'components/Graph/GraphApp/plugins/GraphRendererPlugin';
import { option } from 'fp-ts';
import { pipe } from 'fp-ts/lib/function';

export const hiddenNodes: GraphRendererPlugin = (pluginInterface) => {
  pluginInterface.events.addListener('graphDataRendered', (e) => {
    const { hiddenNodes: nextHiddenNodes, nodesEdgesMap } =
      pluginInterface.getGraphData();

    const modifyEdgeReasonsToHide = (
      hide: boolean,
      edgeId: EdgeId,
      changedNodeId: NodeId,
    ) =>
      pipe(
        option.fromNullable(pluginInterface.getGraphData().edges[edgeId]),
        option.map((edge) => {
          const sourceNodeChanged = edge.source === changedNodeId;
          toggleHideEdge(
            pluginInterface,
            edgeId,
            sourceNodeChanged ? 'SOURCE_NODE_HIDDEN' : 'TARGET_NODE_HIDDEN',
            hide,
          );
        }),
      );

    const prevHiddenNodes = e.prevData?.hiddenNodes ?? new Set<NodeId>();
    if (nextHiddenNodes !== prevHiddenNodes) {
      // biome-ignore lint/complexity/noForEach: iteration over Set
      prevHiddenNodes.forEach((nodeId) => {
        if (!nextHiddenNodes.has(nodeId)) {
          unhideNode(pluginInterface, nodeId, 'USER_HIDDEN');

          // NOTE: Previous node could've been deleted in the meantime, e.g. when uncollapsing.
          // biome-ignore lint/complexity/noForEach: iteration over Set
          nodesEdgesMap[nodeId]?.forEach((edgeId) => {
            modifyEdgeReasonsToHide(false, edgeId, nodeId);
          });
        }
      });
      // biome-ignore lint/complexity/noForEach: iteration over Set
      nextHiddenNodes.forEach((nodeId) => {
        if (!prevHiddenNodes.has(nodeId)) {
          hideNode(pluginInterface, nodeId, 'USER_HIDDEN');
          // biome-ignore lint/complexity/noForEach: iteration over Set
          nodesEdgesMap[nodeId]?.forEach((edgeId) => {
            modifyEdgeReasonsToHide(true, edgeId, nodeId);
          });
        }
      });
    }
  });
  pluginInterface.events.addListener('peekingAtHiddenNodesChange', () => {
    pluginInterface.nodeDisplayObjects.forEach(
      callMethod('updatePeekingAtHiddenNodes'),
    );
  });
};
